import { useSuspenseQuery } from '@tanstack/react-query'
import { ChangeEvent, useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { useRequiredSession } from '@/contexts/auth/session/use-required-session'
import { useCompany } from '@/contexts/company'
import { companiesQueryOptions } from '@/queries/companies'
import { Company } from '@/services/fintalk/services/fintalk-backoffice/companies/list'
import { filterByTextKey } from '@/utils/filter-by-text-key'

export function useCompanySelection() {
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const { userId } = useRequiredSession()

  const { data: companies } = useSuspenseQuery(companiesQueryOptions(userId))
  const { company } = useCompany()

  const navigate = useNavigate()

  const filteredCompanies = useMemo(() => {
    const availableCompaniesToSelect = companies.filter(
      (item) => item._id !== company._id,
    )

    return filterByTextKey(
      availableCompaniesToSelect,
      (item) => item.name,
      searchValue,
    )
  }, [companies, company, searchValue])

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value)
    },
    [],
  )

  const handleClearSearch = useCallback(() => {
    setSearchValue('')
  }, [])

  const handleOpenChange = useCallback((nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen)

    if (!nextIsOpen) {
      setSearchValue('')
    }
  }, [])

  const handleNavigateToCompanyProjects = useCallback(
    (companySelected: Company) => {
      navigate(`/companies/${companySelected._id}/projects`)
    },
    [navigate],
  )

  const handleSelectCompany = useCallback(
    (companySelected: Company) => {
      handleOpenChange(false)

      handleNavigateToCompanyProjects(companySelected)
    },
    [handleOpenChange, handleNavigateToCompanyProjects],
  )

  return {
    hasSingleCompany: companies.length === 1,
    company,
    filteredCompanies,
    isOpen,
    searchValue,
    handleOpenChange,
    handleSearchChange,
    handleClearSearch,
    handleSelectCompany,
  }
}
